import type { AuroraDataset } from '@aurora-pvi/seed';
import { SOURCE_SYSTEMS } from '@aurora-pvi/seed';
import type { ScopeIndex } from './filters';
import type { SeededPersona } from './types';

/** Everything a repository needs, assembled once per `createFixtureRepositories`. */
export interface RepositoryContext {
  dataset: AuroraDataset;
  index: ScopeIndex;
  user: SeededPersona;
}

/**
 * Which registered sources feed which page. Declared per repository so the
 * freshness and failure story is specific: the Finance ERP feed being down must
 * degrade the investment page without touching the process mapping page.
 */
export const PAGE_SOURCES = {
  portfolio: [SOURCE_SYSTEMS.productRegistry, SOURCE_SYSTEMS.cmdb, SOURCE_SYSTEMS.dataHub],
  products: [SOURCE_SYSTEMS.productRegistry, SOURCE_SYSTEMS.dataHub],
  product360: [
    SOURCE_SYSTEMS.productRegistry,
    SOURCE_SYSTEMS.cmdb,
    SOURCE_SYSTEMS.workTracker,
    SOURCE_SYSTEMS.financeErp,
  ],
  discover: [SOURCE_SYSTEMS.productRegistry, SOURCE_SYSTEMS.manual],
  mapping: [SOURCE_SYSTEMS.dataHub, SOURCE_SYSTEMS.productRegistry],
  applications: [SOURCE_SYSTEMS.cmdb, SOURCE_SYSTEMS.dataHub],
  delivery: [SOURCE_SYSTEMS.workTracker, SOURCE_SYSTEMS.serviceDesk, SOURCE_SYSTEMS.observability],
  investment: [
    SOURCE_SYSTEMS.financeErp,
    SOURCE_SYSTEMS.procurement,
    SOURCE_SYSTEMS.contractManager,
  ],
  scenarios: [SOURCE_SYSTEMS.productRegistry, SOURCE_SYSTEMS.financeErp, SOURCE_SYSTEMS.hcm],
  integrations: [SOURCE_SYSTEMS.dataHub],
  lineage: [SOURCE_SYSTEMS.dataHub, SOURCE_SYSTEMS.cmdb],
  governance: [SOURCE_SYSTEMS.dataHub, SOURCE_SYSTEMS.productRegistry],
} as const;

/** Repositories are async because the API client that replaces them will be. */
export function resolved<T>(value: T): Promise<T> {
  return Promise.resolve(value);
}

export function canEdit(user: SeededPersona): boolean {
  return user.personaId !== 'PER-03' && user.personaId !== 'PER-15';
}

export function canApprove(user: SeededPersona): boolean {
  return ['PER-01', 'PER-03', 'PER-05', 'PER-06', 'PER-07', 'PER-17', 'PER-18'].includes(
    user.personaId,
  );
}
